/**
 * The SITE BAR's lockup: the whole mark from `sm:` up, and below that a shorter one
 * cropped after "goonlords".
 *
 * WIDTH IS THE CONSTRAINT, not height. The bar is `py-2` plus its tallest child, and the
 * icon buttons (p-2 around a 15px glyph = 31px) are already taller than the 28px mark —
 * so shrinking the mark buys no vertical space at all. What it buys is horizontal room,
 * and the bar needs it: the sound button stopped folding at `sm:`, and at 360px — an
 * ordinary Android width — the centre group was being pushed straight over the wordmark
 * (measured: a 15px overlap at 360, 55px at 320). The left flank cannot give, because the
 * mark is `shrink-0` and squashing a logo is worse than trimming one.
 *
 * The ".com" is the only part that can go without touching a glyph. The artwork is
 * 2279x511 and its "goonlords" ink ends at x=1609, with the "." starting at 1621; cropping
 * at 1615 sits in that gap, leaving 6px of bearing and no clipped letter. 1615/511 = 3.16,
 * so at 22px tall the visible mark is 70px against the full mark's 125px at 28px tall — 55px
 * back, which clears 360px comfortably and 320px exactly.
 *
 * ONE ASSET, ONE `src`, AND THE CROP IS GEOMETRY. The image is sized by height and simply
 * overflows a narrower box, rather than being a second file or a `src` chosen at runtime —
 * the safe-mode exemption below is only defensible because that `src` is a compile-time
 * constant with no prop behind it, and `src={cond ? a : b}` would end that. `max-w-none`
 * matters: the reset would otherwise clamp the image to the box and squash it instead of
 * cropping it.
 */
export function GoonlordsBarMark() {
  return (
    <span className="inline-flex h-[22px] w-[70px] shrink-0 overflow-hidden sm:h-7 sm:w-[125px]">
      <img
        src="/brand/goonlords-wordmark-crown.png"
        // Exempt for GoonlordsWordmark's reason, and safe for the same one — see there.
        data-safe-exempt="true"
        className="h-full w-auto max-w-none"
        alt="goonlords"
        draggable={false}
      />
    </span>
  );
}

/**
 * The goonlords wordmark: the solid gold crown on the left, lowercase "goonlords"
 * beside it — a horizontal side lockup.
 *
 * White-on-transparent, so DARK BACKGROUNDS ONLY — which is every surface this app
 * has. It replaced the old crown+text lockups (site bar, code popup, admin header,
 * legal header, Editor's Choice byline). The crown alone lives on as a PNG for the
 * places that cannot take a React component — the favicon, the app icon and the
 * share image all point at /brand/crown-128.png.
 *
 * Unlike a bare icon, this image IS the brand text, so it carries a real accessible
 * name rather than alt=""/aria-hidden — without it a screen reader loses the site's
 * name from every surface the mark replaced text on.
 */
export function GoonlordsWordmark({
  height = 24,
  className,
}: {
  height?: number;
  className?: string;
}) {
  return (
    <img
      src="/brand/goonlords-wordmark-crown.png"
      /*
       * EXEMPT FROM SAFE MODE, and safely so.
       *
       * The safe-mode rule blurs every img/video by 28–96px, which at brand-mark sizes
       * does not obscure the mark so much as delete it — the site bar, the code popup
       * and the legal pages would all render a blank space where the name should be.
       * Blurring a logo protects nobody.
       *
       * What makes the exemption safe is that `src` above is a CONSTANT. This component
       * takes no url, so there is no argument anybody can pass that turns it into an
       * uncensored window onto user media — which is the failure the guard in
       * tools/checks/safe-mode-guards.mts exists to prevent, and why that guard polices
       * content surfaces rather than this one. The literal string "true" is required by
       * the CSS on purpose: a React `{false}` would render "false" and match a presence
       * test.
       */
      data-safe-exempt="true"
      height={height}
      // The artwork is 2279x511 (the crowned wordmark, 2026-08-22; the previous mark was
      // 1451x249 and lives on under its old name so cached pages never show a squashed
      // one). Height drives; the width follows the aspect so the mark is never squashed,
      // since every caller sizes it by the row it sits in.
      width={Math.round((height * 2279) / 511)}
      className={className}
      alt="goonlords"
      draggable={false}
    />
  );
}
